import type { SocialHomeBanner } from "@core/home/types";
import { getClientDomain } from "@framework/config";

const mentorMeCampaignSource = "mentor-me";

function isAbsoluteUrl(url: string) {
  return /^[a-z][a-z\d+.-]*:/i.test(url);
}

function normalizeHostname(hostname: string) {
  return hostname
    .toLowerCase()
    .replace(/^www\./, "")
    .split(":")[0];
}

function isExternalCampaignUrl(
  actionUrl: string,
  hostname: string,
  clientId: string,
) {
  if (!isAbsoluteUrl(actionUrl)) return false;

  try {
    const url = new URL(actionUrl);

    if (!["http:", "https:"].includes(url.protocol)) return false;

    const targetHostname = normalizeHostname(url.hostname);
    const localHostnames = [hostname, clientId]
      .filter(Boolean)
      .map(normalizeHostname);

    return !localHostnames.includes(targetHostname);
  } catch {
    return false;
  }
}

function addCampaignAttribution(actionUrl: string, clientId: string) {
  const origin =
    typeof window === "undefined"
      ? "https://mentor-me.local"
      : window.location.origin;

  try {
    const url = new URL(actionUrl, origin);
    url.searchParams.set("utm_source", mentorMeCampaignSource);

    if (clientId) {
      url.searchParams.set("client_id", clientId);
    }

    return isAbsoluteUrl(actionUrl)
      ? url.toString()
      : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return actionUrl;
  }
}

export function useCampaignVisual(banner: SocialHomeBanner) {
  const hostname =
    typeof window === "undefined" ? undefined : window.location.hostname;
  const clientId = getClientDomain(hostname);
  const originalActionUrl = banner.actionUrl;
  const actionUrl = originalActionUrl
    ? addCampaignAttribution(originalActionUrl, clientId)
    : undefined;

  return {
    actionUrl,
    opensInNewTab: originalActionUrl
      ? isExternalCampaignUrl(originalActionUrl, hostname || "", clientId)
      : false,
  };
}
